"use client";

import { useEffect, type JSX } from "react";
import { WA_NUMBERS } from "@/lib/consultation-links";
import { getWhatsAppLink } from "@/lib/whatsapp";

/**
 * Routes every WhatsApp anchor through the site's shared number rotation.
 * This also covers CMS-provided links and CTAs rendered in server components,
 * without having to replace each link with a client-only button.
 */
const isWhatsAppHref = (href: string): boolean => {
  try {
    const { hostname, pathname } = new URL(href, window.location.href);
    return (
      hostname === "wa.me" ||
      ((hostname === "api.whatsapp.com" || hostname === "www.whatsapp.com") &&
        pathname === "/send")
    );
  } catch {
    return false;
  }
};

export const WhatsAppRotationProvider = (): JSX.Element | null => {
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!(event.target instanceof Element)) return;

      const anchor = event.target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.hasAttribute("data-no-whatsapp-rotation")) return;
      if (!isWhatsAppHref(anchor.href)) return;

      const originalUrl = new URL(anchor.href);
      const rotatingUrl = getWhatsAppLink({
        numbers: WA_NUMBERS,
        message: originalUrl.searchParams.get("text") ?? "",
      });

      event.preventDefault();
      if (anchor.target.toLowerCase() === "_blank") {
        window.open(rotatingUrl, "_blank", "noopener,noreferrer");
      } else {
        window.location.assign(rotatingUrl);
      }
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return null;
};
